import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { AmountInput } from "../components/ui/amount-input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "../components/ui/sheet";
import { Textarea } from "../components/ui/textarea";
import { BackdateFields, backdatePayload } from "../components/BackdateFields";
import { useDuplicateGuard, todayIST } from "../components/DuplicateGuard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { formatINR, formatDate, todayISO, waLink } from "../lib/format";
import { openAuthDownload } from "../lib/download";
import { toast } from "sonner";
import {
    FileDown, Printer, RefreshCw, Phone, MessageCircle, Banknote,
} from "lucide-react";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

function ReconCard({ data }) {
    if (!data || !data.rows.length)
        return (
            <Card className="rounded-2xl border-slate-100 p-8 text-center text-sm text-slate-400">
                No data for {data?.date}
            </Card>
        );
    return (
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
            <div className="overflow-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr>
                            {["Employee", "Assigned", "Pending ₹", "Collected #", "Collected ₹", "Missed", "Reminders"].map((h) => (
                                <th key={h} className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {data.rows.map((r) => (
                            <tr key={r.employee} className="hover:bg-slate-50" data-testid={`recon-row-${r.employee}`}>
                                <td className="px-4 py-2.5 font-semibold text-slate-900">{r.employee}</td>
                                <td className="px-4 py-2.5 text-slate-700 num">{r.assigned}</td>
                                <td className="px-4 py-2.5 text-slate-700 num">{formatINR(r.pending_total)}</td>
                                <td className="px-4 py-2.5 num">{r.collected_count}</td>
                                <td className="px-4 py-2.5 num font-semibold text-emerald-700">{formatINR(r.collected_total)}</td>
                                <td className="px-4 py-2.5 num">
                                    {r.missed_visits > 0
                                        ? <Badge className="bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-50">{r.missed_visits}</Badge>
                                        : <span className="text-slate-400">0</span>}
                                </td>
                                <td className="px-4 py-2.5 num text-slate-700">{r.reminders_sent}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

function QuickCollectSheet({ row, onSubmit }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ amount: "", mode: "cash", notes: "" });
    const [backdate, setBackdate] = useState({});
    const { guard, dialog: dupDialog } = useDuplicateGuard();

    async function submit(e) {
        e.preventDefault();
        if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter amount");
        await guard({
            collection_type: "loan", loan_id: row.loan_id, customer_id: row.customer_id, amount: Number(form.amount),
            on_date: backdate.enabled && backdate.collection_datetime ? backdate.collection_datetime.slice(0, 10) : todayIST(),
        }, doSubmit);
    }

    async function doSubmit() {
        setBusy(true);
        try {
            await api.post("/collections", {
                loan_id: row.loan_id,
                customer_id: row.customer_id,
                collection_type: "loan",
                amount: Number(form.amount),
                mode: form.mode,
                notes: form.notes,
                ...backdatePayload(backdate),
            });
            toast.success("Submitted for admin approval");
            setOpen(false);
            setForm({ amount: "", mode: "cash", notes: "" });
            setBackdate({});
            onSubmit?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <>
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button
                    size="sm"
                    className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`ds-collect-${row.loan_id}`}
                >
                    <Banknote className="h-4 w-4 mr-1" /> Collect
                </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl">
                <SheetHeader>
                    <SheetTitle>Collect — {row.customer_name}</SheetTitle>
                </SheetHeader>
                <form onSubmit={submit} className="space-y-3 mt-3">
                    <div className="bg-slate-50 rounded-xl p-3 text-xs">
                        <div className="text-slate-500">Total Interest Pending</div>
                        <div className="num text-rose-700 font-bold text-lg">
                            {formatINR(row.total_interest_pending)}
                        </div>
                    </div>
                    <div>
                        <Label>Amount (₹)</Label>
                        <AmountInput
                            data-testid="ds-collect-amount"
                            required
                            value={form.amount}
                            onChange={(v) => setForm((s) => ({ ...s, amount: v }))}
                            className="h-11 rounded-xl"
                            autoFocus
                        />
                    </div>
                    <div>
                        <Label>Mode</Label>
                        <Select value={form.mode} onValueChange={(v) => setForm((s) => ({ ...s, mode: v }))}>
                            <SelectTrigger className="h-11 rounded-xl" data-testid="ds-collect-mode">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="cash">Cash</SelectItem>
                                <SelectItem value="upi">UPI</SelectItem>
                                <SelectItem value="qr">QR</SelectItem>
                                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Notes</Label>
                        <Textarea
                            data-testid="ds-collect-notes"
                            value={form.notes}
                            onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                            className="rounded-xl"
                        />
                    </div>
                    <BackdateFields value={backdate} onChange={setBackdate} />
                    <Button
                        type="submit"
                        disabled={busy}
                        data-testid="ds-collect-submit"
                        className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800 w-full"
                    >
                        {busy ? "Submitting…" : "Submit for Approval"}
                    </Button>
                </form>
            </SheetContent>
        </Sheet>
        {dupDialog}
        </>
    );
}

export default function DailySheet() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [users, setUsers] = useState([]);
    const [employee, setEmployee] = useState(isAdmin ? "all" : user?.username || "");
    const [date, setDate] = useState(todayISO());
    const [sort, setSort] = useState("due");
    const [data, setData] = useState(null);
    const [recon, setRecon] = useState(null);
    const [busy, setBusy] = useState(false);

    async function load() {
        setBusy(true);
        try {
            const emp = employee === "all" ? null : employee;
            const params = { date, sort };
            if (emp) params.employee = emp;
            const r = await api.get("/reports/daily-sheet", { params });
            setData(r.data);
            if (isAdmin) {
                const rec = await api.get("/reports/reconciliation", { params: { date } });
                setRecon(rec.data);
            }
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (isAdmin) api.get("/users").then((r) => setUsers(r.data));
        load();
        // eslint-disable-next-line
    }, []);

    function buildPdfPath() {
        const params = new URLSearchParams({ date, sort });
        if (employee && employee !== "all") params.set("employee", employee);
        return `/reports/daily-sheet/pdf?${params.toString()}`;
    }

    const monthName = MONTH_NAMES[(date ? new Date(date) : new Date()).getMonth()].toUpperCase();

    return (
        <div className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div
                        className="font-display font-extrabold tracking-tight text-blue-900 leading-none"
                        style={{ fontSize: "clamp(2.5rem, 7vw, 4.5rem)" }}
                        data-testid="daily-sheet-month-name"
                    >
                        {monthName}
                    </div>
                    <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-900 mt-1">
                        Collection Sheet
                    </h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        onClick={() => openAuthDownload(buildPdfPath())}
                        data-testid="daily-sheet-pdf-btn"
                        className="h-11 rounded-xl"
                    >
                        <FileDown className="h-4 w-4 mr-1.5" /> Download PDF
                    </Button>
                    <Button
                        onClick={() => openAuthDownload(buildPdfPath())}
                        data-testid="daily-sheet-print-btn"
                        className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800"
                    >
                        <Printer className="h-4 w-4 mr-1.5" /> Print
                    </Button>
                </div>
            </div>

            <Card className="rounded-2xl border-slate-100 p-4 sm:p-5">
                <div className="grid sm:grid-cols-4 gap-3 items-end">
                    {isAdmin && (
                        <div>
                            <Label>Employee</Label>
                            <Select value={employee} onValueChange={setEmployee}>
                                <SelectTrigger className="h-11 rounded-xl" data-testid="ds-employee-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All employees</SelectItem>
                                    {users.map((u) => (
                                        <SelectItem key={u.id} value={u.username}>
                                            {u.name} ({u.username})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label>Date</Label>
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="h-11 rounded-xl"
                            data-testid="ds-date-input"
                        />
                    </div>
                    <div>
                        <Label>Sort by</Label>
                        <Select value={sort} onValueChange={setSort}>
                            <SelectTrigger className="h-11 rounded-xl" data-testid="ds-sort-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="due">Due date</SelectItem>
                                <SelectItem value="area">Area</SelectItem>
                                <SelectItem value="amount">Amount</SelectItem>
                                {isAdmin && <SelectItem value="employee">Employee</SelectItem>}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button
                        onClick={load}
                        disabled={busy}
                        variant="outline"
                        className={`h-11 rounded-xl tap ${busy ? "font-bold border-blue-900 text-blue-900" : ""}`}
                        data-testid="ds-refresh-btn"
                    >
                        <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />
                        {busy ? "Loading…" : "Apply"}
                    </Button>
                </div>
            </Card>

            <Tabs defaultValue="sheet">
                <TabsList className="bg-white border border-slate-100 rounded-xl">
                    <TabsTrigger value="sheet" data-testid="tab-sheet">
                        Sheet ({data?.count || 0})
                    </TabsTrigger>
                    {isAdmin && (
                        <TabsTrigger value="recon" data-testid="tab-recon">
                            Reconciliation
                        </TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="sheet" className="mt-4">
                    {!data || data.rows.length === 0 ? (
                        <Card className="rounded-2xl border-slate-100 p-10 text-center text-sm text-slate-400">
                            {data ? "No assigned customers for this filter" : "Loading…"}
                        </Card>
                    ) : (
                        <Card className="rounded-2xl border-slate-100 overflow-hidden divide-y divide-slate-100">
                            {data.rows.map((r, i) => (
                                <div
                                    key={r.loan_id}
                                    data-testid={`ds-row-${r.loan_id}`}
                                    className={`px-4 py-3.5 ${r.is_overdue ? "bg-rose-50/40" : ""}`}
                                >
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <Link
                                            to={`/loans/${r.loan_id}`}
                                            className="flex items-start gap-3 min-w-0 flex-1 hover:opacity-80"
                                        >
                                            <span className="num text-xs text-slate-400 pt-0.5">{i + 1}</span>
                                            <div className="min-w-0">
                                                <div className="font-semibold text-slate-900 truncate">
                                                    {r.customer_name}
                                                </div>
                                                <div className="text-[11px] text-slate-500 font-mono truncate">
                                                    {r.customer_code} · {r.loan_code} · {r.loan_type}
                                                </div>
                                                {r.address && (
                                                    <div className="text-[11px] text-slate-500 truncate mt-0.5">
                                                        {r.address}
                                                    </div>
                                                )}
                                            </div>
                                        </Link>
                                        <div className="text-right shrink-0">
                                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                                Total Interest Pending
                                            </div>
                                            <div className="num font-bold text-rose-700">
                                                {formatINR(r.total_interest_pending ?? r.next_interest ?? 0)}
                                            </div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">
                                                {formatDate(r.due_date)}
                                                {r.is_overdue && (
                                                    <Badge className="ml-1 bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100">
                                                        Overdue
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                                        {r.customer_mobile && (
                                            <>
                                                <a
                                                    href={`tel:${r.customer_mobile}`}
                                                    data-testid={`ds-call-${r.loan_id}`}
                                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-blue-900 text-white text-xs font-semibold tap"
                                                >
                                                    <Phone className="h-3.5 w-3.5" /> Call
                                                </a>
                                                <a
                                                    href={waLink(r.customer_mobile, `Hi ${r.customer_name}, please clear your loan ${r.loan_code} interest of ₹${r.total_interest_pending}.`)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`ds-wa-${r.loan_id}`}
                                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold tap"
                                                >
                                                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                                                </a>
                                            </>
                                        )}
                                        <QuickCollectSheet row={r} onSubmit={load} />
                                    </div>
                                </div>
                            ))}
                        </Card>
                    )}
                </TabsContent>

                {isAdmin && (
                    <TabsContent value="recon" className="mt-4">
                        <ReconCard data={recon} />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}
